import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, useApi, type AsyncState, type CreatorSummary, type DashboardData } from '../api';
import {
  Avatar,
  Card,
  EmptyState,
  Icon,
  ErrorBanner,
  isIncomplete,
  Loading,
  QuotaBar,
  StatTile,
  StatusBadge,
  formatDateTime,
  timeUntil,
} from '../components/ui';
import { useT } from '../i18n';

/**
 * Inicio (§16).
 *
 * Prioridad de lectura, de arriba hacia abajo: qué se puede hacer ahora, cuánta cuota
 * queda, qué está esperando a una persona, y qué falló. Es el orden en que un
 * administrador toma decisiones, no el orden en que la API devuelve los datos.
 */
/**
 * Publicaciones con hora puesta que el cron nunca va a tomar.
 *
 * El cron exige que el ítem esté en `APPROVED` o `SCHEDULED`; uno en borrador se queda con su
 * programación en `PENDING` indefinidamente. Nada falla y nada avisa, así que la hora pasa y la
 * publicación simplemente no ocurre.
 *
 * Se agrupan por influencer porque la acción es por influencer: entrar a su pestaña Contenido y
 * aprobar. Listar dieciocho filas —tres plataformas por publicación— sería ruido.
 */
function PublicacionesTrabadas({ upcoming }: { upcoming: DashboardData['upcoming'] }) {
  const { t } = useT();
  const trabadas = upcoming.filter((p) => !p.publicable);
  if (trabadas.length === 0) return null;

  const ahora = new Date().toISOString();
  const vencidas = trabadas.filter((p) => p.scheduledFor <= ahora);
  // Una publicación son tres filas —una por plataforma—, y contar filas asustaría de más.
  const publicaciones = new Set(trabadas.map((p) => p.contentItemId)).size;
  const vencidasUnicas = new Set(vencidas.map((p) => p.contentItemId)).size;

  return (
    <div className="rounded-2xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-4">
      <p className="text-sm">
        <strong>
          {t(publicaciones === 1 ? 'panel.trabadaUna' : 'panel.trabadasVarias', {
            n: publicaciones,
          })}
          {vencidasUnicas > 0 &&
            t(vencidasUnicas === 1 ? 'panel.vencidaUna' : 'panel.vencidasVarias', {
              n: vencidasUnicas,
            })}
          .
        </strong>{' '}
        {t('panel.trabadasPorQue')}
      </p>
    </div>
  );
}

export function Dashboard() {
  const { t } = useT();
  // Las fallidas e incompletas se ocultan por defecto: son ruido de depuración, no trabajo.
  const [showIncomplete, setShowIncomplete] = useState(false);

  /*
    Cada treinta segundos, no cada quince.

    ── Por qué se bajó ─────────────────────────────────────────────────────────
    El sondeo existe para que el estado siga vivo mientras corre un workflow, y eso sigue igual:
    una toma tarda unos cuarenta segundos y una tanda varios minutos, así que quince segundos no
    contaban nada que treinta no cuenten. Lo que sí hacían era el doble de lecturas de D1.

    Es la pantalla que más caro sale: `/api/dashboard` y `/api/creators` leen cada una varias
    tablas, y son las dos únicas que sondean a este ritmo. Con la pausa por visibilidad que hay
    en `useApi` y este número, el gasto de una jornada cae a una fracción.
  */
  const dashboard = useApi<DashboardData>('/api/dashboard', 30_000);
  const creators = useApi<{ creators: CreatorSummary[] }>('/api/creators', 30_000);

  /**
   * Las borradas, para poder recuperarlas.
   *
   * Sin un lugar donde volver a verlas, «borrado lógico» es un detalle de implementación y
   * no una propiedad: quien borró percibe exactamente lo mismo que si se hubiera borrado de
   * verdad. Sin sondeo, porque no cambian solas.
   */
  const deleted = useApi<{
    creators: { id: string; publicName: string; niche: string[]; deletedAt: string }[];
  }>('/api/deleted-creators');

  if (dashboard.loading && !dashboard.data) return <Loading label={t('panel.cargando')} />;
  if (dashboard.error) return <ErrorBanner error={dashboard.error} onRetry={dashboard.reload} />;
  if (!dashboard.data) return null;

  const data = dashboard.data;
  const fatalProblems = data.configProblems.filter((problem) => problem.fatal);
  const warnings = data.configProblems.filter((problem) => !problem.fatal);

  return (
    <div className="space-y-6">
      {fatalProblems.length > 0 && (
        <div
          role="alert"
          className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-4"
        >
          <p className="text-sm font-semibold text-[var(--color-danger)]">
            {t('panel.faltaConfig')}
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {fatalProblems.map((problem) => (
              <li key={problem.key}>
                <code className="font-mono text-xs">{problem.key}</code> — {problem.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t('panel.titulo')}</h1>
          <p className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-[var(--color-ink-muted)]">
            {data.settings.zeroSpendMode ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-ok)]/35 bg-[var(--color-ok)]/10 px-2.5 py-0.5 text-xs font-medium text-[var(--color-ok)]">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
                  {t('panel.modoCero')}
                </span>
                <span>
                  {t('panel.perfil')}{' '}
                  <strong className="text-[var(--color-ink)]">
                    {data.settings.imageQualityProfile}
                  </strong>
                </span>
              </>
            ) : (
              <span className="text-[var(--color-warn)]">
                {t('panel.modoGasto', { monto: data.settings.monthlyBudgetUsd })}
              </span>
            )}
          </p>
        </div>

        <Link
          to="/nueva"
          className="accent-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-[var(--color-accent-ink)] shadow-[0_4px_18px_-4px_var(--color-accent)] transition-all duration-150 hover:brightness-110 hover:shadow-[0_6px_24px_-4px_var(--color-accent)] active:translate-y-px"
        >
          <Icon name="plus" />
          {t('panel.generarNueva')}
        </Link>
      </div>

      {/*
        Dos columnas ya en el teléfono. En una, cada ficha ocupaba media pantalla de alto y las
        cuatro exigían desplazarse para ver un resumen que existe justamente para no tener que
        hacerlo. Son cifras cortas: entran de a dos en 393 px con holgura.
      */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatTile label={t('panel.activas')} value={data.activeCreators} tone="ok" />
        <StatTile
          label={t('panel.esperandoVerificacion')}
          value={data.waitingCreators}
          tone={data.waitingCreators > 0 ? 'warn' : 'neutral'}
        />
        <StatTile
          label={t('panel.tareasPendientes')}
          value={data.pendingTasks}
          tone={data.pendingTasks > 0 ? 'warn' : 'neutral'}
          hint={t(data.pendingTasks > 0 ? 'panel.requierenAccion' : 'panel.nadaPendiente')}
        />
        {/*
          Sólo las que van a salir.

          Contaba todas las programaciones pendientes, y varias no lo estaban de verdad: el cron
          exige que el ítem esté aprobado, así que un borrador con hora se queda pendiente para
          siempre. Seis publicaciones llevaban un día vencidas mientras esta ficha decía «10
          programadas». Un número que incluye lo que nunca va a pasar no informa, tranquiliza.
        */}
        <StatTile
          label={t('panel.programadas')}
          value={data.upcoming.filter((p) => p.publicable).length}
          hint={
            data.upcoming.find((p) => p.publicable)
              ? t('panel.proxima', {
                  cuando: formatDateTime(data.upcoming.find((p) => p.publicable)!.scheduledFor),
                })
              : t('panel.ninguna')
          }
        />
      </div>

      {/*
        Las que están esperando una aprobación que nadie sabe que falta.

        No es un error del sistema: el cron sólo publica ítems aprobados, y eso está bien. Lo
        que estaba mal era el silencio — la programación existía, la hora pasaba, y el tablero
        seguía diciendo que había publicaciones en camino. Seis llevaban un día vencidas.
      */}
      <PublicacionesTrabadas upcoming={data.upcoming} />

      {(deleted.data?.creators.length ?? 0) > 0 && (
        <details className="rounded-2xl border border-[var(--color-border-subtle)] p-4">
          <summary className="cursor-pointer text-sm font-medium">
            {t('panel.borradas')} · {deleted.data?.creators.length}
          </summary>
          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{t('panel.borradasDetalle')}</p>
          <ul className="mt-3 divide-y divide-[var(--color-border-subtle)]">
            {deleted.data?.creators.map((creator) => (
              <li
                key={creator.id}
                className="flex flex-wrap items-center justify-between gap-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium">{creator.publicName}</p>
                  <p className="text-xs text-[var(--color-ink-muted)]">
                    {t('panel.borradaEl', { cuando: formatDateTime(creator.deletedAt) })}
                    {creator.niche.length > 0 ? ` · ${creator.niche.slice(0, 2).join(' · ')}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    void api
                      .post(`/api/creators/${creator.id}/restore`)
                      .then(() => Promise.all([deleted.reload(), creators.reload()]))
                  }
                  className="rounded-xl border border-[var(--color-border-subtle)] px-3 py-1.5 text-xs font-medium transition hover:border-[var(--color-accent)]"
                >
                  {t('panel.recuperar')}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}

      <Card title={t('panel.cuotaHoy')} accent={data.quota.providerExhausted}>
        <div className="space-y-4">
          {/*
            El ledger es una estimación y se midió que va corta. Cuando Cloudflare dice que
            no hay más, eso es lo que vale: mostrar una barra a medio llenar sería mentir.
          */}
          {data.quota.providerExhausted ? (
            <div>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-[var(--color-warn)]">
                  {t('panel.cuotaAgotada')}
                </span>
                <span className="text-xs text-[var(--color-ink-muted)]">
                  {t('panel.seReinicia', {
                    cuando: timeUntil(data.quota.resetsAt),
                    fecha: formatDateTime(data.quota.resetsAt),
                  })}
                </span>
              </div>
              {/* La barra al tope: dejarla en 61 % junto al aviso se contradecía. */}
              <QuotaBar used={1} limit={1} />
              <p className="mt-3 text-sm">
                {t('panel.cuotaAgotadaDetalle', {
                  usados: Math.round(data.quota.used),
                  limite: data.quota.limit ?? 0,
                })}
              </p>
            </div>
          ) : data.quota.limit === null ? (
            /*
              Sin tope no hay barra: una barra necesita un denominador, y acá no hay ninguno que
              no sea inventado. Queda el consumo del día, que es el dato que sí existe.
            */
            <div>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-4">
                <span className="text-sm">
                  {t('panel.sinTope', { usados: Math.round(data.quota.used) })}
                </span>
                <span className="text-xs text-[var(--color-ink-muted)]">
                  {t('panel.contadorSeReinicia', { cuando: timeUntil(data.quota.resetsAt) })}
                </span>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-2 flex items-baseline justify-between gap-4">
                <span className="text-sm">
                  {t('panel.creditosEstimados', {
                    usados: Math.round(data.quota.used),
                    limite: data.quota.limit,
                  })}
                </span>
                <span className="text-xs text-[var(--color-ink-muted)]">
                  {t('panel.seReinicia', {
                    cuando: timeUntil(data.quota.resetsAt),
                    fecha: formatDateTime(data.quota.resetsAt),
                  })}
                </span>
              </div>
              <QuotaBar used={data.quota.used} limit={data.quota.limit} />
            </div>
          )}

          {/*
            El dato más accionable del panel: cuántas influencers más entran hoy.
            Sale del estimador de créditos, no de una suposición.
          */}
          <div className="rounded-xl border border-[var(--color-accent)]/25 bg-[var(--color-accent-soft)] p-4">
            <p className="text-sm">
              {t('panel.costo', {
                perfil: data.costEstimate.profile,
                porImagen: data.costEstimate.neuronsPerImage,
                porInfluencer: data.costEstimate.neuronsPerCreator,
              })}
            </p>
            <p className="mt-1 text-sm">
              {data.quota.providerExhausted
                ? t('panel.noEntraNinguna')
                : t(
                    data.costEstimate.remainingCreatorsToday === 1
                      ? 'panel.entraUna'
                      : 'panel.entranVarias',
                    { n: data.costEstimate.remainingCreatorsToday },
                  )}
            </p>
            <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
              {t('panel.desglose', {
                frontales: data.costEstimate.breakdown.candidates,
                hoja: data.costEstimate.breakdown.identitySheet,
                set: data.costEstimate.breakdown.finalImages,
                texto: data.costEstimate.breakdown.text,
              })}
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {data.quota.byOperation
              // Sin tope, `limit` es null y no hay reparto por operación que dibujar.
              .filter((operation) => (operation.limit ?? 0) > 0)
              .map((operation) => (
                <div key={operation.operation} className="text-xs">
                  <div className="mb-1 flex justify-between">
                    <span className="text-[var(--color-ink-muted)]">{operation.operation}</span>
                    <span className="tabular-nums">
                      {Math.round(operation.used)} / {operation.limit ?? 0}
                    </span>
                  </div>
                  <QuotaBar used={operation.used} limit={operation.limit ?? 0} />
                </div>
              ))}
          </div>

          {/*
            La otra billetera, sólo cuando hay algo que contar.

            Va abajo y aparte, nunca sumada a la de arriba: son dos cosas distintas —un cupo que
            se renueva solo cada día, y plata que no vuelve—. Cuando estaban en el mismo número,
            una tarde en fal dejaba el panel diciendo «169.877 de 10.000» y «entran 0 influencers
            más hoy» a una cuenta que tenía casi entera su cuota gratuita de Cloudflare.
          */}
          {data.quota.pago.used > 0 && (
            <div className="rounded-xl border border-[var(--color-line)] p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{t('panel.modelosDePago')}</span>
                <span className="text-sm tabular-nums">
                  USD {data.quota.pago.usd.toFixed(2)}
                  {data.quota.pago.limit !== null && (
                    <span className="text-[var(--color-ink-muted)]">
                      {' '}
                      {t('panel.creditosDe', {
                        usados: Math.round(data.quota.pago.used),
                        limite: data.quota.pago.limit,
                      })}
                    </span>
                  )}
                </span>
              </div>
              {data.quota.pago.limit !== null && (
                <div className="mt-2">
                  <QuotaBar used={data.quota.pago.used} limit={data.quota.pago.limit} />
                </div>
              )}
              <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{t('panel.bolsaAparte')}</p>
            </div>
          )}
        </div>
      </Card>

      {/*
        Las influencers ocupan el ancho entero, y lo de al lado bajó.

        Estaban en media pantalla, con los errores y las colas a la derecha. Cada fila tiene que
        entrar el nombre, el nicho, los seguidores, los perfiles, las tareas y el estado — en
        media pantalla eso se aprieta o se corta, y lo primero que se pierde por truncado es el
        nicho, que está al lado del número.

        Lo de la derecha no necesitaba esa mitad: son tres listas cortas que casi siempre dicen
        que no hay nada.
      */}
      <CreatorList
        state={creators}
        showIncomplete={showIncomplete}
        onToggleIncomplete={() => setShowIncomplete((value) => !value)}
      />

      {/*
      `items-start`: cada tarjeta mide lo que necesita.

      Sin esto, la rejilla reparte a sus hijos con `stretch` y la tarjeta más corta de cada
      fila se estira hasta el alto de su vecina. Medido: «Influencers» quedaba con 538 px de
      vacío debajo de la última fila porque al lado había tres tarjetas apiladas. No es un
      problema de relleno ni de márgenes —el borde estaba donde la rejilla dijo—, y por eso
      se lee como que el diseño está corrido.

      El precio es que las columnas terminan disparejas. Es lo correcto: dicen dónde termina
      cada cosa, en vez de dibujar un marco alrededor de nada.
      */}
      <div className="grid items-start gap-6 lg:grid-cols-3">
        <div className="space-y-6">
          <Card title={t('panel.erroresRecientes')}>
            {data.errors.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-muted)]">{t('panel.sinErrores')}</p>
            ) : (
              <ul className="space-y-3">
                {data.errors.map((error) => (
                  <li key={`${error.platform}-${error.errorCode}`} className="text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{error.platform}</span>
                      <span className="text-xs text-[var(--color-ink-muted)]">
                        ×{error.count} · {formatDateTime(error.lastSeenAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                      {error.errorMessage}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/*
            La cola diferida es visible por diseño: §2 pide mostrar cuándo se reinicia cada
            cuota. Trabajo esperando en silencio es trabajo que parece perdido.
          */}
          <Card title={t('panel.diferidas')}>
            {data.deferred.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-muted)]">{t('panel.nadaEnEspera')}</p>
            ) : (
              <ul className="space-y-3">
                {data.deferred.map((operation) => (
                  <li key={operation.id} className="text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{operation.operation}</span>
                      <span className="text-xs text-[var(--color-ink-muted)]">
                        {timeUntil(operation.runAfter)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                      {operation.reason}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title={t('panel.proveedoresTexto')}>
            <ul className="space-y-2">
              {data.providers.map((provider) => (
                <li key={provider.name} className="flex items-center justify-between text-sm">
                  <span>{provider.name}</span>
                  <span
                    className={
                      provider.available
                        ? 'text-xs font-medium text-[var(--color-ok)]'
                        : 'text-xs text-[var(--color-ink-muted)]'
                    }
                  >
                    {t(provider.available ? 'panel.disponible' : 'panel.sinConfigurar')}
                  </span>
                </li>
              ))}
            </ul>
            {warnings.length > 0 && (
              <p className="mt-3 text-xs text-[var(--color-ink-muted)]">{warnings[0]?.message}</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * Listado de influencers.
 *
 * Cada una con su foto: reconocer una cara es inmediato y leer un nombre no. Las que
 * fallaron o nunca llegaron a generar nada se ocultan detrás de un filtro — pero el filtro
 * dice cuántas son, así que nada desaparece en silencio.
 */
function CreatorList({
  state,
  showIncomplete,
  onToggleIncomplete,
}: {
  state: AsyncState<{ creators: CreatorSummary[] }>;
  showIncomplete: boolean;
  onToggleIncomplete: () => void;
}) {
  const { t, idioma } = useT();
  if (state.loading && !state.data) {
    return (
      <Card title={t('panel.influencers')}>
        <Loading />
      </Card>
    );
  }

  const all = state.data?.creators ?? [];
  const incompleteCount = all.filter((creator) => isIncomplete(creator.status)).length;
  const visible = showIncomplete ? all : all.filter((creator) => !isIncomplete(creator.status));

  return (
    <Card
      title={t('panel.influencers')}
      actions={
        incompleteCount > 0 ? (
          <button
            type="button"
            onClick={onToggleIncomplete}
            aria-pressed={showIncomplete}
            className="text-xs text-[var(--color-ink-muted)] underline-offset-2 hover:underline"
          >
            {t(
              incompleteCount === 1
                ? showIncomplete
                  ? 'panel.ocultarFallidaUna'
                  : 'panel.mostrarFallidaUna'
                : showIncomplete
                  ? 'panel.ocultarFallidas'
                  : 'panel.mostrarFallidas',
              { n: incompleteCount },
            )}
          </button>
        ) : undefined
      }
    >
      {visible.length === 0 ? (
        <EmptyState
          title={t(all.length === 0 ? 'panel.sinInfluencers' : 'panel.ningunaCompleta')}
          description={
            all.length === 0
              ? t('panel.pulsaGenerar')
              : t('panel.todasIncompletas', { n: incompleteCount })
          }
        />
      ) : (
        <ul className="divide-y divide-[var(--color-border-subtle)]">
          {visible.map((creator) => (
            /*
              El enlace al perfil va FUERA del <Link> de la fila: un <a> dentro de otro no es
              HTML válido y el navegador rompe el anidado por su cuenta, con resultados que
              cambian entre navegadores. Por eso la fila es un grid y no un solo enlace
              envolviendo todo.
            */
            <li
              key={creator.id}
              /*
                Apilada en el teléfono, en línea de `sm` para arriba.

                Las insignias de la derecha —arroba, tareas, estado— estaban en un contenedor
                `shrink-0`: se negaban a encoger y estiraban la fila hasta 774 px en una
                pantalla de 393. Medido con el navegador, no a ojo. En una pantalla angosta no
                hay dos columnas que repartir: hay una, y las insignias van debajo del nombre.
              */
              className="-mx-2 flex flex-col gap-2 rounded-xl px-2 py-3 transition hover:bg-[var(--color-accent-soft)] sm:flex-row sm:items-center sm:gap-3"
            >
              <Link
                to={`/influencer/${creator.id}`}
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                <Avatar name={creator.publicName} url={creator.avatarUrl} />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{creator.publicName}</p>
                  <p className="truncate text-xs text-[var(--color-ink-muted)]">
                    {/*
                      Los seguidores primero, porque son lo que ordena esta lista.

                      Un orden que no se puede verificar mirando la pantalla parece roto: sin el
                      número, la lista se ve mezclada al azar y no hay forma de darse cuenta de
                      que está ordenada por algo.

                      Las que nunca se midieron no dicen «0 seguidores»: dirían que no tienen a
                      nadie, y lo que pasa es que todavía no se sabe.
                    */}
                    {creator.seguidores !== null && (
                      <>
                        <span className="font-medium text-[var(--color-ink)] tabular-nums">
                          {creator.seguidores.toLocaleString(idioma)}
                        </span>{' '}
                        {t('panel.seguidores')} ·{' '}
                      </>
                    )}
                    {creator.niche.join(' · ') || t('panel.sinNicho')}
                  </p>
                </div>
              </Link>

              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                {creator.profiles.map((profile) => (
                  <a
                    key={profile.platform}
                    href={profile.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    title={t('panel.abrirPerfil', {
                      usuario: profile.username,
                      plataforma: profile.platform,
                    })}
                    // Medían 22 px. La regla global de `styles.css` cubre a `button` y no a
                    // `a`, porque un enlace puede vivir en medio de una frase. Éstas no: son
                    // píldoras sueltas, y en un teléfono se fallan al pulsarlas.
                    className="inline-flex items-center rounded-full border border-[var(--color-border-subtle)] px-2.5 py-0.5 text-xs text-[var(--color-ink-muted)] transition hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] [@media(pointer:coarse)]:min-h-11"
                  >
                    @{profile.username}
                  </a>
                ))}
                {creator.pendingTaskCount > 0 && (
                  <Link
                    to={`/influencer/${creator.id}`}
                    className="inline-flex items-center rounded-full bg-[var(--color-warn)]/15 px-2.5 py-0.5 text-xs font-medium text-[var(--color-warn)] [@media(pointer:coarse)]:min-h-11"
                  >
                    {t(creator.pendingTaskCount === 1 ? 'panel.tareaUna' : 'panel.tareas', {
                      n: creator.pendingTaskCount,
                    })}
                  </Link>
                )}
                <StatusBadge status={creator.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
